import * as React from "react";
import { NavLink } from "react-router-dom";
import { usePermissions } from "@refinedev/core";

import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Icon } from "@/components/ui/icon";
import {
  NAV_ITEMS,
  NAV_SECTION_ADMIN,
  type NavItem,
} from "@/components/layout/nav";
import { hasAdminAccess } from "@/lib/admin-access";

interface SidebarNavProps {
  /** Optional click handler, used to close the mobile sheet on navigation. */
  onNavigate?: () => void;
}

/** The brand + navigation list, shared by the desktop rail and mobile sheet. */
export function SidebarNav({ onNavigate }: SidebarNavProps) {
  // Gate the Admin group by the caller's permissions (visibility only — the
  // backend stays authoritative). Under dev-auth the identity IS admin, so the
  // group always shows on staging; for real non-admin sessions it is hidden.
  const { data: perms } = usePermissions<string[]>();
  const showAdmin = hasAdminAccess(perms);
  const items = React.useMemo(
    () =>
      showAdmin
        ? NAV_ITEMS
        : NAV_ITEMS.filter((item) => item.section !== NAV_SECTION_ADMIN),
    [showAdmin],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Brand block. No inner box — just whitespace and the wordmark. Stays
          fixed at the top of the rail (aligned to the slim h-12 header); only
          the nav list below it scrolls. */}
      <div className="flex h-12 shrink-0 items-center gap-2 px-5">
        <Icon name="brand" size={20} className="text-primary" aria-hidden />
        <span className="text-sm font-semibold tracking-tight">
          AOS Portal
        </span>
      </div>
      <ScrollArea className="flex-1">
        <nav className="flex flex-col gap-0.5 px-3 pb-6" aria-label="Primary">
          {items.map((item, i) => {
            const prev = items[i - 1];
            const showHeading = !!item.section && item.section !== prev?.section;
            return (
              <React.Fragment key={item.to}>
                {showHeading && (
                  <div className="px-3 pb-2 pt-6 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {item.section}
                  </div>
                )}
                <SidebarLink item={item} onNavigate={onNavigate} />
              </React.Fragment>
            );
          })}
        </nav>
      </ScrollArea>
    </div>
  );
}

function SidebarLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          // §4.1 calm nav row: h-9, no resting background, hover = bg-accent.
          // Active = tint + the only accent in the rail (a 2px left edge, now black).
          "relative flex h-9 items-center gap-3 rounded-md pl-3 pr-3 text-sm transition-colors",
          "hover:bg-accent hover:text-foreground",
          isActive
            ? "bg-accent font-medium text-foreground before:absolute before:inset-y-1.5 before:-left-0 before:w-0.5 before:rounded-full before:bg-primary before:content-['']"
            : "text-muted-foreground",
        )
      }
    >
      <Icon name={item.icon} size={16} className="shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

/** Fixed desktop sidebar rail (hidden on small screens). */
export function Sidebar({ className }: { className?: string }) {
  return (
    <aside
      className={cn(
        // §3.1 rule 4 (structural fold): one mechanism only — a single border-r.
        // feedback #5: pin the rail full-height (sticky top-0 h-screen) so it
        // never scrolls with the page; only `<main>` (overflow-y-auto) scrolls.
        // The brand stays fixed at top; the nav list overflows internally via
        // the inner ScrollArea if the viewport is short.
        "sticky top-0 hidden h-screen w-60 shrink-0 border-r border-border bg-background md:block",
        className,
      )}
    >
      <SidebarNav />
    </aside>
  );
}
